import { BarChart2, Menu, Settings, Users, LogOut, Calendar, ScanEye , FileBarChart,GitBranch, ChevronsLeft, ChevronsRight, GripVertical } from "lucide-react";
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { useTheme } from '../../../../shared/common/ThemeContext';
import { fetchAuth } from '../../../../shared/common/api';
import { useIsMobile } from '../../../../shared/common/useIsMobile';
import { useSeason, buildSeasonOptions } from "../../../../shared/common/SeasonContext";
import { useMe } from "../../../../shared/common/useMe";
import { canSeeSidebarPlayersList, canSeeSidebarUpdatePlayersInfos } from "../../../../shared/common/roleAccess.js";
import { encodeId } from "../../../../shared/common/hashid";
import AlertsCenterModal from "../../../../shared/common/AlertsCenterModal";
import AlertsBellButton, { AlertsBellIcon } from "../../../../shared/common/AlertsBellButton";
import { useAlertsCenter } from "../../../../shared/common/useAlertsCenter";

const SIDEBAR_ITEMS = [
  { name: "Overview", icon: BarChart2, color: "#6366f1", href: "/overview" },
  { name: "Players", icon: Users, color: "#3B82F6", href: "/playersperfs" },
  { name: "Simulation", icon: GitBranch, color: "#3B82F6", href: "/simulation" },
  { name: "Calendar", icon: Calendar, color: "#9333EA", href: "/calendar" },
  { name: "Reports", icon: FileBarChart, color: "#F59E0B", href: "/reports" },
  { name: "Update Players Infos", icon: ScanEye, color: "#10B981", href: "/update-players-infos" },
  { name: "Settings", icon: Settings, color: "#6EE7B7", href: "/settings" },
];

const COLLAPSED_SIDEBAR_WIDTH = 80;
const DEFAULT_EXPANDED_SIDEBAR_WIDTH = 256;
const MIN_SIDEBAR_WIDTH = 80;
const MAX_SIDEBAR_WIDTH = 360;
const LABELS_VISIBILITY_WIDTH = 170;
const SEASON_VISIBILITY_WIDTH = 220;

export default function Sidebar({ handleLogout }) {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [logo, setLogo] = useState(null);
  const [sidebarWidth, setSidebarWidth] = useState(COLLAPSED_SIDEBAR_WIDTH);
  const [expandedSidebarWidth, setExpandedSidebarWidth] = useState(DEFAULT_EXPANDED_SIDEBAR_WIDTH);
  const [isResizingSidebar, setIsResizingSidebar] = useState(false);
  const [isResizeHandleHovered, setIsResizeHandleHovered] = useState(false);
  const resizeCleanupRef = useRef(() => {});
  const isMobile = useIsMobile();
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const { season, setSeason } = useSeason();
  const seasons = buildSeasonOptions();

  // Permissions — show role-gated items while /me is loading; hide only once denied.
  const { me, loading: loadingPerms } = useMe();
  const canSeeUpdatePlayersInfos = canSeeSidebarUpdatePlayersInfos(me, { loading: loadingPerms });
  const canSeePlayersList = canSeeSidebarPlayersList(me, { loading: loadingPerms });

  const visibleItems = useMemo(() => {
    return SIDEBAR_ITEMS.filter((item) => {
      if (item.href === "/update-players-infos") return canSeeUpdatePlayersInfos;
      if (item.href === "/playersperfs") return canSeePlayersList;
      return true;
    });
  }, [canSeeUpdatePlayersInfos, canSeePlayersList]);

  const {
    isOpen: isAlertsOpen,
    openModal: openAlertsModal,
    closeModal: closeAlertsModal,
    alerts,
    filter: alertsFilter,
    changeFilter: changeAlertsFilter,
    loading: alertsLoading,
    error: alertsError,
    unreadCount,
    maxUnreadSeverity,
    refreshUnreadCount,
    reloadCurrent,
    markRead: markAlertRead,
    markUnread: markAlertUnread,
    removeAlert: removeAlert,
  } = useAlertsCenter();

  const openAlertDetails = async (alert) => {
    if (!alert?.id) return;

    if (!alert?.coach_state?.is_read) {
      await markAlertRead(alert.id);
    }

    const playerId = alert?.player_id || alert?.player?.id;
    closeAlertsModal();

    if (playerId) {
      navigate(`/players/${encodeId(playerId)}?alert=${encodeURIComponent(alert.id)}`);
    }
  };

  const clampSidebarWidth = useCallback((width) => {
    return Math.min(MAX_SIDEBAR_WIDTH, Math.max(MIN_SIDEBAR_WIDTH, width));
  }, []);

  const isSidebarOpen = sidebarWidth > COLLAPSED_SIDEBAR_WIDTH + 8;

  const toggleSidebar = useCallback(() => {
    setSidebarWidth((currentWidth) => {
      if (currentWidth > COLLAPSED_SIDEBAR_WIDTH + 8) {
        return COLLAPSED_SIDEBAR_WIDTH;
      }
      return clampSidebarWidth(expandedSidebarWidth);
    });
  }, [clampSidebarWidth, expandedSidebarWidth]);

  const startSidebarResize = useCallback((event) => {
    if (event.button !== 0) return;

    resizeCleanupRef.current();

    const startX = event.clientX;
    const startWidth = sidebarWidth;

    event.preventDefault();
    setIsResizingSidebar(true);
    setIsResizeHandleHovered(true);
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";

    const onMouseMove = (moveEvent) => {
      const nextWidth = clampSidebarWidth(startWidth + (moveEvent.clientX - startX));
      setSidebarWidth(nextWidth);
      if (nextWidth > COLLAPSED_SIDEBAR_WIDTH + 8) {
        setExpandedSidebarWidth(nextWidth);
      }
    };

    const cleanup = () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
      setIsResizingSidebar(false);
      setIsResizeHandleHovered(false);
      resizeCleanupRef.current = () => {};
    };

    const onMouseUp = () => {
      setSidebarWidth((currentWidth) => {
        if (currentWidth <= COLLAPSED_SIDEBAR_WIDTH + 12) {
          return COLLAPSED_SIDEBAR_WIDTH;
        }
        return currentWidth;
      });
      cleanup();
    };

    resizeCleanupRef.current = cleanup;
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  }, [clampSidebarWidth, sidebarWidth]);

  useEffect(() => {
    return () => {
      resizeCleanupRef.current();
    };
  }, []);

  // === Compactage automatique selon hauteur ===
  // normal: >= 780px ; compact: 620–779px ; iconOnly: < 620px
  const [density, setDensity] = useState("normal"); // "normal" | "compact" | "iconOnly"
  useEffect(() => {
    const update = () => {
      const h = window.innerHeight;
      if (h < 620) setDensity("iconOnly");
      else if (h < 780) setDensity("compact");
      else setDensity("normal");
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const response = await fetchAuth(`/clubs/logo`);
        const data = await response.json();
        if (response.ok) setLogo(data.logo);
      } catch {}
    })();
  }, []);

  // Helpers classes selon densité
  const navItemPad = density === "normal" ? "p-4" : density === "compact" ? "p-2.5" : "p-2";
  const navItemText = density === "normal" ? "text-sm" : density === "compact" ? "text-xs" : "text-[11px]";
  const showLabels = density !== "iconOnly" && sidebarWidth >= LABELS_VISIBILITY_WIDTH;

  const seasonLabelCls = density === "normal" ? "text-xs" : "text-[11px]";
  const seasonSelectPad = density === "normal" ? "py-1 px-2" : "py-0.5 px-1.5";
  const seasonBlockHidden = density === "iconOnly" || sidebarWidth < SEASON_VISIBILITY_WIDTH; // on masque le sélecteur pour gagner de la place

  const logoWidth = density === "normal" ? "w-28" : density === "compact" ? "w-24" : "w-20";

  return (
    <>
      {/* 🌐 Desktop Sidebar */}
      {!isMobile && (
        <motion.div
          className={`relative z-10 flex-shrink-0 h-screen sticky top-0 ${isResizingSidebar ? "" : "transition-[width] duration-300 ease-out"}`}
          style={{ width: `${sidebarWidth}px` }}
        >
          {/* ⛔️ plus d'overflow-y-auto : on évite la scrollbar */}
          <div className={`h-full ${isDarkMode ? 'bg-gray-800' : 'bg-white'} p-3 flex flex-col border-r ${isDarkMode ? 'border-gray-700' : 'border-gray-300'} overflow-hidden`}>
            {showLabels && (
              <div className="flex items-center justify-center px-1 py-1">
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <img src={isDarkMode ? "/logo.png" : "/logo-light.png"} alt="PrediTwins" className="h-5" />
                </motion.div>
              </div>
            )}

            <nav className={`${showLabels ? "mt-4" : "mt-2"} flex-grow flex flex-col gap-1`}>
              <AlertsBellButton
                variant="nav"
                unreadCount={unreadCount}
                maxUnreadSeverity={maxUnreadSeverity}
                onClick={openAlertsModal}
                isDarkMode={isDarkMode}
                density={density}
                showLabel={showLabels}
                navItemPad={navItemPad}
                navItemText={navItemText}
                iconSize={showLabels ? 20 : 22}
              />

              {visibleItems.map((item) => (
                <Link key={item.href} to={item.href}>
                  <motion.div
                    className={`flex items-center ${navItemPad} ${navItemText} font-medium rounded-lg transition-colors
                                ${isDarkMode ? 'hover:bg-gray-700 text-white' : 'hover:bg-blue-200 text-black'}`}
                  >
                    <item.icon size={showLabels ? 20 : 22} style={{ color: item.color, minWidth: "20px" }} />
                    <AnimatePresence>
                      {showLabels && (
                        <motion.span
                          className="ml-3 whitespace-nowrap"
                          initial={{ opacity: 0, width: 0 }}
                          animate={{ opacity: 1, width: "auto" }}
                          exit={{ opacity: 0, width: 0 }}
                          transition={{ duration: 0.15 }}
                        >
                          {item.name}
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </motion.div>
                </Link>
              ))}
            </nav>

            {/* ---------- Sélecteur de saison ---------- */}
            {isSidebarOpen && !seasonBlockHidden && (
              <div className="my-3 px-2">
                <label className={`block mb-1 ${seasonLabelCls} ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                  Season
                </label>
                <select
                  value={season}
                  onChange={(e) => setSeason(e.target.value)}
                  className={`w-full rounded-md border ${navItemText} ${seasonSelectPad} focus:outline-none focus:ring-1 focus:ring-indigo-500 ${
                    isDarkMode ? "bg-gray-700 border-gray-500 text-gray-100" : "bg-white border-gray-400 text-gray-900"
                  }`}
                >
                  {seasons.map((s) => (
                    <option key={s} value={s} className={isDarkMode ? "bg-gray-700 text-gray-100" : "bg-white text-gray-900"}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="flex justify-between items-center">
              {showLabels && (
                <motion.div className="ml-2" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  {!!logo && <img src={logo} alt="logo" className={`${logoWidth} text-right`} />}
                </motion.div>
              )}
            </div>

            <div className="mt-auto">
              <motion.div
                className={`flex items-center ${navItemPad} ${navItemText} font-medium rounded-lg transition-colors cursor-pointer
                            ${isDarkMode ? 'hover:bg-gray-700 text-red-400' : 'hover:bg-red-100 text-red-700'}`}
                onClick={handleLogout}
              >
                <LogOut size={showLabels ? 20 : 22} style={{ color: "#ef4444", minWidth: "20px" }} />
                <AnimatePresence>
                  {showLabels && (
                    <motion.span
                      className="ml-3 whitespace-nowrap"
                      initial={{ opacity: 0, width: 0 }}
                      animate={{ opacity: 1, width: "auto" }}
                      exit={{ opacity: 0, width: 0 }}
                      transition={{ duration: 0.15 }}
                    >
                      Logout
                    </motion.span>
                  )}
                </AnimatePresence>
              </motion.div>
            </div>
          </div>

          <div className="absolute right-0 top-0 z-20 h-full">
            <div
              onMouseDown={startSidebarResize}
              onMouseEnter={() => setIsResizeHandleHovered(true)}
              onMouseLeave={() => {
                if (!isResizingSidebar) setIsResizeHandleHovered(false);
              }}
              className="absolute right-0 top-0 h-full w-3 cursor-col-resize"
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize sidebar"
              title="Drag to resize"
            >
              <motion.div
                className={`absolute bottom-3 right-0 top-3 rounded-full transition-all ${
                  isDarkMode ? "bg-gray-600/90" : "bg-gray-300"
                }`}
                animate={{
                  width: isResizingSidebar || isResizeHandleHovered ? 3 : 2,
                  opacity: isResizingSidebar || isResizeHandleHovered ? 1 : 0.55,
                }}
              />
            </div>

            <motion.button
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
              onClick={toggleSidebar}
              className={`absolute right-0 top-1/2 z-30 h-14 w-4 -translate-y-1/2 rounded-full border backdrop-blur-sm transition-all ${
                isDarkMode
                  ? "border-gray-600/80 bg-gray-700/85 text-gray-200 hover:bg-gray-600/90"
                  : "border-gray-300/90 bg-white/90 text-gray-700 hover:bg-white"
              } ${isResizingSidebar || isResizeHandleHovered ? "ring-2 ring-indigo-500/25" : ""}`}
              aria-label={isSidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
              title={isSidebarOpen ? "Collapse" : "Expand"}
            >
              <div className="flex h-full w-full flex-col items-center justify-center">
                <GripVertical size={10} className="opacity-55" />
                {isSidebarOpen ? <ChevronsLeft size={10} /> : <ChevronsRight size={10} />}
              </div>
            </motion.button>
          </div>
        </motion.div>
      )}

      {/* 📱 Mobile menu conservé tel quel */}
      {isMobile && (
        <div className="fixed top-4 left-4 z-50">
          <button
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            className={`p-2 rounded-full shadow-lg ${isDarkMode ? 'bg-gray-700 text-white' : 'bg-white text-black'}`}
          >
            <Menu />
          </button>

          <AnimatePresence>
            {showMobileMenu && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className={`absolute mt-2 left-0 w-48 rounded-lg shadow-lg p-4 ${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-black'}`}
              >
                <button
                  onClick={() => {
                    setShowMobileMenu(false);
                    openAlertsModal();
                  }}
                  className="mb-3 flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left hover:underline"
                >
                  <span className="flex items-center gap-2">
                    <AlertsBellIcon unreadCount={unreadCount} maxUnreadSeverity={maxUnreadSeverity} />
                    Alerts
                  </span>
                  {unreadCount > 0 && (
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold text-white ${
                      maxUnreadSeverity === "critical"
                        ? "bg-red-600"
                        : maxUnreadSeverity === "high"
                          ? "bg-orange-500"
                          : "bg-red-500"
                    }`}>
                      {unreadCount > 99 ? "99+" : unreadCount}
                    </span>
                  )}
                </button>

                {visibleItems.map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={() => setShowMobileMenu(false)}
                    className="flex items-center mb-3 space-x-2 hover:underline"
                  >
                    <item.icon size={18} style={{ color: item.color }} />
                    <span>{item.name}</span>
                  </Link>
                ))}
                <hr className="my-2" />
                <button onClick={handleLogout} className="flex items-center space-x-2 text-red-500 hover:underline">
                  <LogOut size={18} />
                  <span>Logout</span>
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      <AlertsCenterModal
        isOpen={isAlertsOpen}
        onClose={closeAlertsModal}
        alerts={alerts}
        loading={alertsLoading}
        error={alertsError}
        filter={alertsFilter}
        onFilterChange={changeAlertsFilter}
        unreadCount={unreadCount}
        isDarkMode={isDarkMode}
        onRefresh={() => refreshUnreadCount().then(reloadCurrent)}
        onOpenAlert={openAlertDetails}
        onMarkRead={markAlertRead}
        onMarkUnread={markAlertUnread}
        onDeleteAlert={removeAlert}
      />
    </>
  );
}
